import React, { useEffect, useMemo } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { MapPin, Clock, ShieldCheck, Star, ChevronRight, Plane } from 'lucide-react';
import { ROUTES, buildBookingUrl } from './seoConfig';
import { QuotePreviewWidget } from '../../components/seo/QuotePreviewWidget';

function upsertMeta(name, content, isProperty = false) {
  const attr = isProperty ? 'property' : 'name';
  let el = document.querySelector(`meta[${attr}="${name}"]`);
  if (!el) { el = document.createElement('meta'); el.setAttribute(attr, name); document.head.appendChild(el); }
  el.setAttribute('content', content);
}
function upsertCanonical(url) {
  let el = document.querySelector('link[rel="canonical"]');
  if (!el) { el = document.createElement('link'); el.setAttribute('rel', 'canonical'); document.head.appendChild(el); }
  el.setAttribute('href', url);
}
function upsertJsonLd(id, obj) {
  let el = document.getElementById(id);
  if (!el) { el = document.createElement('script'); el.type = 'application/ld+json'; el.id = id; document.head.appendChild(el); }
  el.textContent = JSON.stringify(obj);
}

// Marketplace vehicle categories – mirrors backend db.vehicle_categories.
// Note: `lug` is display-normalized to at least equal `pax` (1 Gepäckstück pro Person).
const PRICE_ROWS = [
  { id: 'economy',  de: 'Economy',  en: 'Economy',  pax: 4,  lug: 4 },
  { id: 'comfort',  de: 'Komfort',  en: 'Comfort',  pax: 4,  lug: 4 },
  { id: 'business', de: 'Business', en: 'Business', pax: 3,  lug: 3 },
  { id: 'premium',  de: 'Premium',  en: 'Premium',  pax: 3,  lug: 3 },
  { id: 'vip',      de: 'VIP',      en: 'VIP',      pax: 3,  lug: 4 },
  { id: 'suv',      de: 'SUV',      en: 'SUV',      pax: 6,  lug: 6 },
  { id: 'van',      de: 'Van',      en: 'Van',      pax: 8,  lug: 8 },
  { id: 'minibus',  de: 'Kleinbus', en: 'Minibus',  pax: 16, lug: 16 },
];

export default function RouteLanding({ lang = 'de' }) {
  const { from, to } = useParams();
  const navigate = useNavigate();
  const key = `${from}--${to}`;
  const r = ROUTES[key];

  const T = useMemo(() => ((de, en) => (lang === 'en' ? en : de)), [lang]);

  // Pre-computed price rows array — avoids inline .map() inside <tbody>
  // which the preview-only Emergent instrumentation wraps with <span>,
  // producing HTML hydration warnings. Production build is unaffected.
  const priceRowElements = useMemo(() => {
    if (!r?.prices) return null;
    const arr = [];
    for (const row of PRICE_ROWS) {
      const p = r.prices[row.id];
      if (p == null) continue;
      arr.push(
        React.createElement('tr', { key: row.id, 'data-testid': `price-row-${row.id}` },
          React.createElement('td', { className: 'px-4 py-3 font-medium text-slate-800' }, T(row.de, row.en)),
          React.createElement('td', { className: 'px-4 py-3 text-slate-600' },
            `${row.pax} ${T('Pers.', 'pax')} · ${row.lug} ${T('Gepäck', 'bags')}`),
          React.createElement('td', { className: 'px-4 py-3 text-right font-bold text-slate-900' }, `€${p}`),
        )
      );
    }
    return arr;
  }, [r, T]);

  useEffect(() => {
    if (!r) return;
    const isEn = lang === 'en';
    const title = isEn
      ? `${r.from} → ${r.to} Transfer | Drivers bid from €${r.price}`
      : `Transfer ${r.from} → ${r.to} | Fahrer bieten ab €${r.price}`;
    const desc = isEn
      ? `Reliable transfer from ${r.from} to ${r.to}. Verified drivers send you individual offers from €${r.price}. Distance ${r.km} km, duration ${Math.floor(r.min/60)}h ${r.min%60}m. Meet & Greet ✓ Free cancellation ✓ Compare offers ►`
      : `Zuverlässiger Transfer ${r.from} nach ${r.to}. Verifizierte Fahrer senden dir individuelle Angebote ab €${r.price}. Distanz ${r.km} km, Fahrzeit ${Math.floor(r.min/60)}h ${r.min%60}m. Meet & Greet ✓ Kostenlose Stornierung ►`;
    document.title = title;
    upsertMeta('description', desc);
    upsertMeta('og:title', title, true);
    upsertMeta('og:description', desc, true);
    upsertMeta('og:type', 'website', true);
    upsertCanonical(`https://366transfer.com${isEn ? '/en' : ''}/transfer/${from}/${to}`);
    document.documentElement.lang = lang;

    // Schema.org
    upsertJsonLd('schema-product', {
      '@context': 'https://schema.org', '@type': 'Product',
      name: `${r.from} → ${r.to} Transfer`,
      description: desc,
      brand: { '@type': 'Brand', name: '366Transfer' },
      offers: { '@type': 'AggregateOffer', priceCurrency: 'EUR', lowPrice: r.price, highPrice: r.price * 2.5, offerCount: 5, availability: 'https://schema.org/InStock' },
    });
    upsertJsonLd('schema-faq', {
      '@context': 'https://schema.org', '@type': 'FAQPage',
      mainEntity: faqs(r, isEn).map(f => ({ '@type': 'Question', name: f.q, acceptedAnswer: { '@type': 'Answer', text: f.a } })),
    });
    upsertJsonLd('schema-breadcrumb', {
      '@context': 'https://schema.org', '@type': 'BreadcrumbList',
      itemListElement: [
        { '@type': 'ListItem', position: 1, name: isEn ? 'Home' : 'Startseite', item: `https://366transfer.com${isEn ? '/en' : ''}` },
        { '@type': 'ListItem', position: 2, name: r.from, item: `https://366transfer.com${isEn ? '/en' : ''}/flughafen-${from.replace('-airport','')}-transfer` },
        { '@type': 'ListItem', position: 3, name: r.to, item: `https://366transfer.com${isEn ? '/en' : ''}/transfer/${from}/${to}` },
      ],
    });
    return () => ['schema-product', 'schema-faq', 'schema-breadcrumb'].forEach(id => document.getElementById(id)?.remove());
  }, [r, from, to, lang]);

  if (!r) {
    return <div className="min-h-screen flex items-center justify-center p-12 text-center">
      <div><h1 className="text-2xl font-bold mb-2">Route not found</h1>
      <Link to="/" className="text-orange-500 hover:underline">Zurück zur Startseite</Link></div>
    </div>;
  }

  const isEn = lang === 'en';
  const hours = Math.floor(r.min / 60), mins = r.min % 60;
  const bookUrl = buildBookingUrl(r.fromSlug, r.toSlug);

  return (
    <div className="min-h-screen bg-slate-50" data-testid={`route-landing-${key}`}>
      <nav className="max-w-6xl mx-auto px-4 pt-4 text-xs text-slate-500">
        <Link to="/" className="hover:text-orange-500">{T('Startseite', 'Home')}</Link>
        <span className="mx-2">/</span>
        <span className="text-slate-700">{r.from} → {r.to}</span>
      </nav>

      <section className="max-w-6xl mx-auto px-4 pt-8 pb-12">
        <div className="grid lg:grid-cols-5 gap-8 items-start">
          <div className="lg:col-span-3">
            <div className="inline-flex items-center gap-1.5 px-3 py-1 bg-orange-100 text-orange-700 rounded-full text-xs font-semibold mb-4">
              <Plane size={12} /> {T('Fahrer bieten – du wählst', 'Drivers bid – you choose')}
            </div>
            <h1 className="text-3xl sm:text-4xl lg:text-5xl font-extrabold text-slate-900 leading-tight mb-4" data-testid="route-h1">
              {T(`Transfer ${r.from} nach ${r.to} – Fahrer bieten, du wählst`, `${r.from} → ${r.to} Transfer – drivers bid, you choose`)}
            </h1>
            <p className="text-lg text-slate-700 leading-relaxed mb-6">
              {T(
                `Zuverlässiger Transfer von ${r.from} nach ${r.to}. Verifizierte Fahrer in Ägypten senden dir individuelle Angebote ab €${r.price} – du vergleichst in Sekunden und wählst das beste. Meet & Greet am Terminal, Kindersitze kostenlos, Flug-Tracking inklusive.`,
                `Reliable transfer from ${r.from} to ${r.to}. Verified drivers in Egypt send you individual offers from €${r.price} – compare in seconds and pick the best. Meet & Greet at the terminal, child seats free, flight tracking included.`
              )}
            </p>
            <button onClick={() => navigate(bookUrl)} className="px-6 py-3 bg-orange-500 hover:bg-orange-600 text-white rounded-lg font-bold text-sm shadow-md" data-testid="route-cta-hero">
              {T('Angebote anfragen →', 'Request offers →')}
            </button>
          </div>
          <aside className="lg:col-span-2 bg-white rounded-2xl border border-slate-200 shadow-sm p-6 space-y-3">
            <p className="text-xs font-semibold uppercase text-slate-400 mb-2">{T('Route-Fakten', 'Route facts')}</p>
            <div className="flex items-center gap-3"><MapPin size={16} className="text-orange-500"/><span className="text-sm text-slate-700"><b>{r.km} km</b> {T('Distanz','distance')}</span></div>
            <div className="flex items-center gap-3"><Clock size={16} className="text-orange-500"/><span className="text-sm text-slate-700"><b>{hours}h {mins}m</b> {T('Fahrzeit','duration')}</span></div>
            <div className="flex items-center gap-3"><ShieldCheck size={16} className="text-orange-500"/><span className="text-sm text-slate-700">{T('Ab €' + r.price + ' (Fahrer bieten)', 'From €' + r.price + ' (drivers bid)')}</span></div>
          </aside>
        </div>
      </section>

      {/* CRO Quote Preview Widget — gated by fromCoords availability (Sprint CRO-1 prototype) */}
      {r.fromCoords && r.toCoords && (
        <section className="max-w-6xl mx-auto px-4 pb-8" data-testid="quote-widget-section">
          <div className="max-w-xl mx-auto">
            <QuotePreviewWidget route={r} />
          </div>
        </section>
      )}

      {/* Landscape / highlight */}
      <section className="bg-white border-y border-slate-100 py-12">
        <div className="max-w-4xl mx-auto px-4">
          <h2 className="text-xl sm:text-2xl font-bold text-slate-900 mb-4">{T(`Was Sie auf der Strecke ${r.from} → ${r.to} erwartet`, `What to expect on the ${r.from} → ${r.to} route`)}</h2>
          <p className="text-slate-700 leading-relaxed mb-3">
            {T(
              `Die Strecke führt Sie ${r.land} und deckt eine Distanz von ${r.km} Kilometern ab. Bei normaler Verkehrslage beträgt die Fahrzeit etwa ${hours} Stunden und ${mins} Minuten. ${r.highlight}`,
              `The route takes you ${r.land} covering ${r.km} kilometres. Under normal traffic conditions the drive takes about ${hours} hours ${mins} minutes. ${r.highlight}`
            )}
          </p>
          <p className="text-slate-700 leading-relaxed">
            {T(
              `Alle Fahrzeuge in unserem Netzwerk sind klimatisiert und modern – wichtig bei Wüstenrouten mit Temperaturen von bis zu 40°C. Ihr Fahrer wartet mit Namensschild in der Ankunftshalle und trackt Ihren Flug automatisch. Bei Verspätung wartet er ohne Aufpreis bis zu 90 Minuten. Wir empfehlen die Online-Vorabbuchung, damit Sie nicht am Terminal um den Preis feilschen müssen.`,
              `All vehicles in our network are air-conditioned and modern – essential for desert routes where temperatures can reach 40°C. Your driver waits with a name sign in the arrival hall and tracks your flight automatically. If your flight is delayed the driver waits up to 90 minutes for free. We recommend booking online in advance to avoid haggling at the terminal.`
            )}
          </p>
        </div>
      </section>

      {/* Price table by vehicle type (8 categories) */}
      {r.prices && (
        <section className="max-w-4xl mx-auto px-4 py-12" data-testid="route-price-table">
          <h2 className="text-xl sm:text-2xl font-bold text-slate-900 mb-2 text-center">
            {T(`Ab-Preise für ${r.from} → ${r.to}`, `Starting prices for ${r.from} → ${r.to}`)}
          </h2>
          <p className="text-sm text-slate-600 text-center mb-6">
            {T(
              'Richtwerte pro Fahrzeugtyp. Verifizierte Fahrer bieten dir individuelle Angebote – oft günstiger als der Richtwert.',
              'Reference prices per vehicle type. Verified drivers send you individual offers – often cheaper than the reference.'
            )}
          </p>
          <div className="overflow-x-auto rounded-xl border border-slate-200 bg-white">
            <table className="w-full text-sm">
              <thead className="bg-slate-50">
                <tr>
                  <th className="text-left px-4 py-3 font-semibold text-slate-700">{T('Fahrzeugtyp', 'Vehicle type')}</th>
                  <th className="text-left px-4 py-3 font-semibold text-slate-700">{T('Kapazität', 'Capacity')}</th>
                  <th className="text-right px-4 py-3 font-semibold text-slate-700">{T('Ab', 'From')}</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {priceRowElements}
              </tbody>
            </table>
          </div>
          <p className="text-xs text-slate-500 mt-3 text-center">
            {T(
              'Die tatsächlichen Fahrer-Angebote können variieren. Alle Preise inkl. Meet & Greet und kostenlose Stornierung bis 24 h vorher.',
              'Actual driver offers may vary. All prices include Meet & Greet and free cancellation up to 24 h in advance.'
            )}
          </p>
        </section>
      )}

      {/* Marketplace USP */}
      <section className="max-w-6xl mx-auto px-4 py-12">
        <h2 className="text-xl sm:text-2xl font-bold text-slate-900 mb-6 text-center">{T('Warum 366Transfer für diese Route?', 'Why 366Transfer for this route?')}</h2>
        <div className="grid md:grid-cols-3 gap-4">
          {[
            { t: T('Fahrer bieten – du wählst', 'Drivers bid – you choose'), d: T(`Statt Festpreis vom Vermittler bekommst du echte Angebote ab €${r.price} von mehreren verifizierten Fahrern und wählst das beste.`, `Instead of a middleman price you get real offers from €${r.price} from multiple verified drivers and pick the best.`) },
            { t: T('Verifizierte Fahrer', 'Verified drivers'), d: T('Alle Fahrer werden vor Aufnahme geprüft: Lizenz, Fahrzeugschein, Versicherung, Strafregister.', 'All drivers are vetted before joining: license, vehicle registration, insurance, criminal record.') },
            { t: T('Kostenlose Stornierung', 'Free cancellation'), d: T('Storniere bis 24 h vor Abholung kostenlos – volle Rückerstattung, keine Fragen.', 'Cancel up to 24 h before pickup free of charge – full refund, no questions.') },
          ].map(u => (
            <div key={u.t} className="bg-white rounded-xl border border-slate-100 p-5">
              <ShieldCheck size={18} className="text-orange-500 mb-2" />
              <h3 className="font-bold text-slate-800 mb-1">{u.t}</h3>
              <p className="text-sm text-slate-600">{u.d}</p>
            </div>
          ))}
        </div>
      </section>

      {/* FAQ */}
      <section className="bg-white border-y border-slate-100 py-12">
        <div className="max-w-4xl mx-auto px-4">
          <h2 className="text-xl sm:text-2xl font-bold text-slate-900 mb-6 text-center">{T('Häufige Fragen zur Strecke','Route FAQ')}</h2>
          <div className="space-y-3">
            {faqs(r, isEn).map((f, i) => (
              <details key={i} className="bg-slate-50 rounded-lg border border-slate-100 p-4">
                <summary className="font-medium text-slate-800 cursor-pointer">{f.q}</summary>
                <p className="mt-2 text-sm text-slate-600">{f.a}</p>
              </details>
            ))}
          </div>
        </div>
      </section>

      {/* Related routes */}
      <section className="max-w-6xl mx-auto px-4 py-12">
        <h2 className="text-xl sm:text-2xl font-bold text-slate-900 mb-6">{T(`Beliebte Ziele ab ${r.from}`, `Popular destinations from ${r.from}`)}</h2>
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
          {Object.entries(ROUTES).filter(([k, v]) => v.fromSlug === r.fromSlug && k !== key).slice(0, 6).map(([k, v]) => (
            <Link key={k} to={`${isEn ? '/en' : ''}/transfer/${v.fromSlug}/${v.toSlug}`} className="bg-white rounded-xl border border-slate-100 p-4 hover:border-orange-200 transition-colors">
              <p className="font-medium text-slate-800 text-sm">{v.to}</p>
              <p className="text-xs text-slate-500 mt-0.5">€{v.price} · {v.km} km</p>
              <ChevronRight size={14} className="text-orange-500 mt-2" />
            </Link>
          ))}
        </div>
      </section>

      <section className="py-12 bg-gradient-to-br from-orange-500 to-orange-600 text-white text-center">
        <div className="max-w-3xl mx-auto px-4">
          <h2 className="text-2xl font-bold mb-3">{T(`Bereit für Ihren Transfer ${r.from} → ${r.to}?`, `Ready for your ${r.from} → ${r.to} transfer?`)}</h2>
          <p className="mb-5 text-orange-50">{T('Kostenlose Angebote in 60 Sekunden. Keine Verpflichtung.', 'Free offers in 60 seconds. No commitment.')}</p>
          <button onClick={() => navigate(bookUrl)} className="px-8 py-3 bg-white text-orange-600 rounded-lg font-bold shadow-lg" data-testid="route-cta-bottom">
            {T('Jetzt kostenlose Angebote anfragen', 'Request free offers now')} →
          </button>
        </div>
      </section>
    </div>
  );
}

function faqs(r, isEn) {
  const hours = Math.floor(r.min / 60), mins = r.min % 60;
  const p = r.prices || {};
  if (isEn) return [
    { q: `How long does the ${r.from} to ${r.to} transfer take?`, a: `Under normal traffic conditions the drive takes about ${hours}h ${mins}m over a distance of ${r.km} km.` },
    { q: `How much does a transfer from ${r.from} to ${r.to} cost?`, a: `Verified drivers send you individual offers. Reference prices: Economy from €${p.economy || r.price}, Comfort from €${p.comfort || Math.round(r.price*1.3)}, VIP from €${p.vip || Math.round(r.price*2)}, Van from €${p.van || Math.round(r.price*1.3)}, Minibus from €${p.minibus || Math.round(r.price*2.5)}.` },
    { q: 'How does the bidding work?', a: 'You submit your route in the booking form. Verified drivers in the area send you individual offers within minutes. You compare price, vehicle, driver rating and choose the best one.' },
    { q: 'What if my flight is delayed?', a: 'We track your flight automatically. The driver waits up to 90 minutes past the new landing time free of charge.' },
    { q: 'Where do I meet my driver?', a: 'Your driver waits with a sign showing your name in the arrival hall. Exact position sent by SMS 2h before landing.' },
    { q: 'Are child seats available?', a: 'Yes, infant seats, child seats and boosters are free of charge. Just mention age and count when booking.' },
    { q: 'Can I pay in Euros?', a: 'Yes, online payment in Euros by credit card, PayPal. Drivers accept Euros, USD or EGP in cash.' },
    { q: 'Can I cancel my booking?', a: 'Free cancellation up to 24 hours before the ride with full refund. Later cancellations 50%, no-show 100%.' },
    { q: 'Are drivers insured?', a: 'All drivers and vehicles are licensed and fully insured. We vet every partner before joining our network.' },
  ];
  return [
    { q: `Wie lange dauert der Transfer ${r.from} nach ${r.to}?`, a: `Bei normaler Verkehrslage etwa ${hours} Stunden und ${mins} Minuten über eine Distanz von ${r.km} km.` },
    { q: `Was kostet ein Transfer von ${r.from} nach ${r.to}?`, a: `Verifizierte Fahrer senden dir individuelle Angebote. Richtwerte: Economy ab €${p.economy || r.price}, Komfort ab €${p.comfort || Math.round(r.price*1.3)}, VIP ab €${p.vip || Math.round(r.price*2)}, Van ab €${p.van || Math.round(r.price*1.3)}, Kleinbus ab €${p.minibus || Math.round(r.price*2.5)}.` },
    { q: 'Wie funktioniert das Bieter-System?', a: 'Du gibst deine Route im Buchungsformular ein. Verifizierte Fahrer in der Region senden dir innerhalb weniger Minuten individuelle Angebote. Du vergleichst Preis, Fahrzeug, Fahrer-Bewertung und wählst das beste.' },
    { q: 'Was passiert bei Flugverspätung?', a: 'Wir tracken deinen Flug automatisch. Der Fahrer wartet bis zu 90 Minuten nach der neuen Landezeit ohne Aufpreis.' },
    { q: 'Wo treffe ich meinen Fahrer?', a: 'Dein Fahrer wartet mit Namensschild in der Ankunftshalle. Exakte Position per SMS 2 Stunden vor Landung.' },
    { q: 'Sind Kindersitze verfügbar?', a: 'Ja, Baby-Schalen, Kindersitze und Booster sind kostenlos. Alter und Anzahl bitte bei der Buchung angeben.' },
    { q: 'Kann ich in Euro bezahlen?', a: 'Ja, Online-Zahlung in Euro per Kreditkarte oder PayPal. Fahrer akzeptieren zusätzlich Euro, USD oder EGP bar.' },
    { q: 'Kann ich meine Buchung stornieren?', a: 'Kostenlose Stornierung bis 24 Stunden vor der Fahrt – volle Rückerstattung. Später 50 %, No-Show 100 %.' },
    { q: 'Sind die Fahrer versichert?', a: 'Alle Fahrer und Fahrzeuge sind lizenziert und voll versichert. Wir prüfen jeden Partner vor Aufnahme.' },
  ];
}
